import { Injectable } from '@angular/core';
import { getMessaging, getToken, onMessage } from 'firebase/messaging';
import { environment } from 'src/environments/environment';
import { getApps, initializeApp } from 'firebase/app';
import { UtilitiesService } from '../utilities/utilities.service';
import {
  registerNotificationModel,
  unregisterNotificationModel,
} from '../../model/registerNotification.model';
import { DeviceDetectorService, DeviceInfo } from 'ngx-device-detector';
import { LocalStorageService } from '../local-storage/local-storage.service';
import { map, Observable } from 'rxjs';
import { UrlEnum } from '../../enum/url.enum';
import { HttpRequestService } from '../http-request/http-request.service';
import { CommonSharedServiceService } from '../../shared-services/common-shared-service/common-shared-service.service';

@Injectable({
  providedIn: 'root',
})
export class NotificationService {
  private registerNotificationModel: registerNotificationModel;
  private deviceInfo: DeviceInfo;

  private url = UrlEnum.SHOHAY_NOTIFICATION;
  private app: any;
  private messaging: any;

  constructor(
    private readonly utilitiesService: UtilitiesService,
    private readonly deviceService: DeviceDetectorService,
    private readonly localstorageService: LocalStorageService,
    private readonly httpRequestService: HttpRequestService,
    private readonly commonSharedService: CommonSharedServiceService
  ) {
    this.registerNotificationModel = new registerNotificationModel();
    this.initializeFirebase();
  }

  private message: string = '';

  private initializeFirebase() {
    if (!this.app) {
      this.app = initializeApp(environment.firebaseConfig);
      this.messaging = getMessaging(this.app);
    }
  }

  public registerServiceWorker(): Promise<void> {
    return new Promise((resolve, reject) => {
      if ('serviceWorker' in navigator) {
        navigator.serviceWorker
          .register('firebase-messaging-sw.js')
          .then((registration) => {
            console.log('Service Worker registered successfully');
            this.requestPermission().then(resolve).catch(reject);
          })
          .catch(reject);
      } else {
        reject('Service Worker not supported');
      }
    });
  }

  private requestPermission(): Promise<void> {
    return new Promise((resolve, reject) => {
      Notification.requestPermission()
        .then((permission) => {
          if (permission === 'granted') {
            console.log('Notification permission granted.');

            const app = initializeApp(environment.firebaseConfig);
            const messaging = getMessaging(app);

            if (!this.messaging) {
              this.initializeFirebase();
            }

            getToken(messaging, {
              vapidKey: environment.firebaseConfig.vapidKey,
            })
              .then((currentToken) => {
                if (currentToken) {
                  console.log('Hurrah We got the token!!');

                  this.registerNotificationServer(
                    currentToken,
                    this.localstorageService.getSessionUser().id
                  )
                    .then(resolve)
                    .catch(reject);
                } else {
                  reject('No token available');
                }
              })
              .catch(reject);
          } else {
            reject('Permission not granted');
          }
        })
        .catch(reject);
    });
  }

  public registerNotificationServer(
    regFirebaseToken: any,
    userId?: string
  ): Promise<void> {
    return new Promise((resolve, reject) => {
      this.deviceInfo = this.deviceService.getDeviceInfo();
      this.registerNotificationModel.reg_token = regFirebaseToken;
      this.registerNotificationModel.device = this.deviceInfo.userAgent;
      this.registerNotificationModel.device_id = this.deviceInfo.userAgent;
      this.registerNotificationModel.user_id =
        userId ?? this.localstorageService.getSessionUser()?.id;
      this.registerNotificationModel.type = this.deviceInfo.deviceType;

      this.registerNotification(this.registerNotificationModel).subscribe(
        (result) => {
          if (result) {
            this.localstorageService.setIsNotificationRegistered('yes');
            this.registerNotificationModel = new registerNotificationModel();
            console.log('register done to notification server.');
            resolve(); // Resolve the promise on success
          } else {
            this.localstorageService.setIsNotificationRegistered('no');
            reject('Registration failed: result was false');
          }
        },
        (error) => {
          this.localstorageService.setIsNotificationRegistered('no');
          console.error('Error registering notification server:', error);
          reject(error); // Reject the promise on error
        }
      );
    });
  }

  public listenFromFireBaseServer() {
    console.log('Listening for Firebase messages...');
    if (!this.messaging) {
      this.initializeFirebase();
    }

    const messaging = getMessaging();
    onMessage(messaging, (payload: any) => {
      console.log('Message received. ', payload);
      if (payload.notification) return;

      this.message = payload;
      const notificationData = payload?.data['data']
        ? JSON.parse(payload?.data['data'])
        : null;
        
      this.utilitiesService.showSwalWithToast(
        'Notification!',
        notificationData?.title
          ? notificationData.title
          : 'You have a new notification.',
        'info'
      );
      this.commonSharedService.triggerNotificationChanged();
    });
  }

  public registerNotification(body: any): Observable<any> {
    return this.httpRequestService
      .post(`${this.url}/register`, body)
      .pipe(map((x: any) => this.utilitiesService.responseHandler(x)));
  }

  public unregisterNotification(body: any): Observable<any> {
    return this.httpRequestService
      .post(`${this.url}/unregister`, body)
      .pipe(map((x: any) => this.utilitiesService.responseHandler(x)));
  }

  public getAllNotificationByUser(userId: string): Observable<any> {
    return this.httpRequestService
      .get(this.url + '/get-all-by-user/' + userId)
      .pipe(map((x: any) => this.utilitiesService.responseHandler(x)));
  }

  public seenSpecificNotifications(user_id:string,notificationId: string): Observable<any> {
    return this.httpRequestService
      .patch(`${this.url}/users/${user_id}/messages/${notificationId}/update-is-seen`, {})
      .pipe(map((x: any) => this.utilitiesService.responseHandler(x)));
  }

  public seenAllNotifications(userId: string): Observable<any> {
    return this.httpRequestService
      .patch(`${this.url}/users/${userId}/all-is-seen`, {})
      .pipe(map((x: any) => this.utilitiesService.responseHandler(x)));
  }
}
